import { useNavigate } from "react-router-dom";
import { PencilLine } from "lucide-react";
import { getSystemIconPath } from "../../../../lib/systemOfRecord";
import {
  CONTROL_OPTRO_IMPORT_PATH,
  useOptroImportLauncher,
} from "../../../../hooks/useOptroImportLauncher";
import { useOptroBulkImportEnabled } from "../../../../hooks/useOptroBulkImportEnabled";
import { OptroConnectDialog } from "../optroImport/components/OptroConnectDialog";

interface ControlCreationOptionsProps {
  title: string;
  subtitle: string;
}

// The ways to get controls into Midship: bulk import from Optro (behind the
// optro-bulk-import flag), or the manual/AI creation form. Shared between the
// controls list's empty state ("Welcome to Midship") and the Create New
// Control page.
export const ControlCreationOptions = ({
  title,
  subtitle,
}: ControlCreationOptionsProps) => {
  const navigate = useNavigate();
  const optroBulkImportEnabled = useOptroBulkImportEnabled();
  const { launchImport, dialogProps } = useOptroImportLauncher({
    wizardPath: CONTROL_OPTRO_IMPORT_PATH,
    enabled: optroBulkImportEnabled,
  });
  return (
    <div className="py-12 max-w-2xl mx-auto text-left">
      <h2 className="text-2xl font-semibold text-center text-stone-900">
        {title}
      </h2>
      <p className="text-sm text-stone-500 text-center mt-2 mb-8">{subtitle}</p>

      {optroBulkImportEnabled && (
        <button
          type="button"
          onClick={launchImport}
          className="w-full text-left border rounded-xl p-5 mb-4 hover:bg-stone-50 transition"
        >
          <div className="flex items-start gap-4">
            <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
              <img
                src={getSystemIconPath("auditboard")}
                alt=""
                className="h-5 w-5"
              />
            </div>
            <div>
              <div className="text-base font-semibold text-stone-900">
                Import controls from Optro
              </div>
              <div className="text-sm text-stone-600">
                Connect Optro and bulk import your controls. Midship configures
                each one from its Optro control data and historical workpapers.
              </div>
            </div>
          </div>
        </button>
      )}

      <button
        type="button"
        onClick={() => navigate("/audit/controls/v2/create/manual")}
        className="w-full text-left border rounded-xl p-5 hover:bg-stone-50 transition"
      >
        <div className="flex items-start gap-4">
          <div className="h-10 w-10 bg-stone-100 rounded-lg flex items-center justify-center">
            <PencilLine size={20} />
          </div>
          <div>
            <div className="text-base font-semibold text-stone-900">
              Import controls manually
            </div>
            <div className="text-sm text-stone-600">
              Generate a control from your documentation with AI, bulk import
              from an RCM spreadsheet, or build one from scratch.
            </div>
          </div>
        </div>
      </button>

      <OptroConnectDialog {...dialogProps} />
    </div>
  );
};
